import type { IInfoPanelAction, IInfoPanelItemData } from '../types';

import { Box, BlockStack, Button, InlineStack, Text, Thumbnail } from '@shopify/polaris';

import styles from '../GInfoPanel.module.css';

interface GInfoPanelRightColumnProps {
  info?: IInfoPanelItemData;
  width?: string;
  minHeight?: string;
  primaryAction?: IInfoPanelAction;
  secondaryAction?: IInfoPanelAction;
}

export const GInfoPanelRightColumn = ({
  info,
  width = '100%',
  minHeight = '100%',
  primaryAction,
  secondaryAction,
}: GInfoPanelRightColumnProps) => {
  const hasActions = primaryAction || secondaryAction;

  return (
    <Box width={width} minHeight={minHeight} background="bg-surface-secondary">
      <div className="flex flex-col justify-start items-start h-full">
        <div className="flex-1 h-full">
          <Box
            padding="300"
            background="bg-surface-secondary"
            borderRadius="200"
            position="sticky"
            insetBlockStart="0"
          >
            {info ? <GInfoPanelContent info={info} /> : <GInfoPanelEmptyState />}
          </Box>
        </div>

        {hasActions && (
          <div className={styles.StickyBottom}>
            <Box
              padding="300"
              background="bg-surface-secondary"
              borderBlockStartWidth="025"
              borderColor="border"
            >
              <InlineStack gap="200" align="end">
                {secondaryAction && (
                  <Button
                    onClick={secondaryAction.onAction}
                    disabled={secondaryAction.disabled}
                    loading={secondaryAction.loading}
                  >
                    {secondaryAction.content}
                  </Button>
                )}
                {primaryAction && (
                  <Button
                    variant="primary"
                    onClick={primaryAction.onAction}
                    disabled={primaryAction.disabled}
                    loading={primaryAction.loading}
                  >
                    {primaryAction.content}
                  </Button>
                )}
              </InlineStack>
            </Box>
          </div>
        )}
      </div>
    </Box>
  );
};

const GInfoPanelContent = ({ info }: { info: IInfoPanelItemData }) => (
  <BlockStack gap="400">
    <BlockStack gap="100">
      {info.thumbnail && <Thumbnail source={info.thumbnail} alt="Preview" size="large" />}

      {info.title && (
        <Box>
          {typeof info.title === 'string' ? (
            <Text as="h2" variant="headingMd" fontWeight="semibold">
              {info.title}
            </Text>
          ) : (
            info.title
          )}
        </Box>
      )}

      {info.description && (
        <Box>
          {typeof info.description === 'string' ? (
            <Text as="p" variant="bodyMd" fontWeight="regular">
              {info.description}
            </Text>
          ) : (
            info.description
          )}
        </Box>
      )}
    </BlockStack>

    {info.helperText && (
      <BlockStack gap="100">
        {info.helperText.title && (
          <Box>
            {typeof info.helperText.title === 'string' ? (
              <Text as="p" variant="bodySm" fontWeight="medium">
                {info.helperText.title}
              </Text>
            ) : (
              info.helperText.title
            )}
          </Box>
        )}

        {info.helperText.description && (
          <Box>
            {typeof info.helperText.description === 'string' ? (
              <Text as="p" variant="bodyMd">
                {info.helperText.description}
              </Text>
            ) : (
              info.helperText.description
            )}
          </Box>
        )}
      </BlockStack>
    )}
  </BlockStack>
);

const GInfoPanelEmptyState = () => (
  <Box paddingBlock="400" minHeight="200px">
    <InlineStack align="center" blockAlign="center">
      <Text as="p" variant="bodyMd" tone="subdued">
        Select an item to view details
      </Text>
    </InlineStack>
  </Box>
);
